import type { SpeechRequest } from '@/api/types';
import { speechRequestKey } from '@/speech/SpeechPrefetch';
import { OFFLINE_MODE } from '@/config/offline';
import { OFFLINE_PHRASES } from '@/speech/offlineSpeech';
import { OFFLINE_PITCHES, OFFLINE_SPEEDS } from '@/speech/offlineAudioPackage';
import type { AppEdge, AppNode, SayTextData } from './types';
import {
  DEFAULT_SAY_TEXT_PITCH_SCALE, DEFAULT_SAY_TEXT_SPEAKER, DEFAULT_SAY_TEXT_SPEED_SCALE,
  SAY_TEXT_PITCH_SCALE_MAX, SAY_TEXT_PITCH_SCALE_MIN, SAY_TEXT_SPEED_SCALE_MAX,
  SAY_TEXT_SPEED_SCALE_MIN, SAY_TEXT_VOICES,
} from './config/sayTextVoice';

export function resolveSpeechRequest(value: unknown, data: SayTextData): SpeechRequest | string {
  if (value === undefined) return 'さきに Text Edit を つないでね';
  const text = String(value);
  if (!text.trim()) return '発話する文字を入力してください';
  if ([...text].length > 200) return '発話する文字は200文字以内にしてください';
  if (OFFLINE_MODE) {
    if (!OFFLINE_PHRASES.some((phrase) => phrase.text === text)) return 'オフラインでは Text Edit の定型文を選択してください';
  }
  const speaker = data.speaker ?? DEFAULT_SAY_TEXT_SPEAKER;
  const speedScale = data.speedScale ?? DEFAULT_SAY_TEXT_SPEED_SCALE;
  const pitchScale = data.pitchScale ?? DEFAULT_SAY_TEXT_PITCH_SCALE;
  if (!SAY_TEXT_VOICES.some((voice) => voice.speaker === speaker)) return '声の種類を選び直してください';
  if (!Number.isFinite(speedScale) || speedScale < SAY_TEXT_SPEED_SCALE_MIN || speedScale > SAY_TEXT_SPEED_SCALE_MAX) {
    return `声のスピードは${SAY_TEXT_SPEED_SCALE_MIN}〜${SAY_TEXT_SPEED_SCALE_MAX}で指定してください`;
  }
  if (!Number.isFinite(pitchScale) || pitchScale < SAY_TEXT_PITCH_SCALE_MIN || pitchScale > SAY_TEXT_PITCH_SCALE_MAX) {
    return `声の高さは${SAY_TEXT_PITCH_SCALE_MIN}〜${SAY_TEXT_PITCH_SCALE_MAX}で指定してください`;
  }
  if (OFFLINE_MODE && (!OFFLINE_SPEEDS.some((speed) => speed === speedScale)
    || !OFFLINE_PITCHES.some((pitch) => pitch === pitchScale))) {
    return 'オフライン用の速度（0.5、1、2）と高さ（-0.15、0、0.15）を選択してください';
  }
  return { text, speaker, speedScale, pitchScale };
}

export function collectSpeechPlan(nodes: AppNode[], edges: AppEdge[], initialValue?: unknown): SpeechRequest[] {
  const requests = new Map<string, SpeechRequest>();
  let steps = 0;
  const visit = (graph: AppNode[], links: AppEdge[], input: string | undefined, depth: number): Array<string | undefined> => {
    if (depth > 32) throw new Error('TTS先読み: Functionの階層が深すぎます');
    const start = graph.find((node) => node.type === 'start' || node.type === 'input');
    if (!start) return [undefined];
    const byId = new Map(graph.map((node) => [node.id, node]));
    const outs = new Map<string, AppEdge[]>();
    links.forEach((edge) => outs.set(edge.source, [...(outs.get(edge.source) ?? []), edge]));
    const pending = [{ id: start.id, value: input }];
    const visited = new Set<string>();
    const outputs = new Set<string | undefined>();
    while (pending.length) {
      const current = pending.pop()!;
      const key = JSON.stringify([current.id, current.value]);
      if (visited.has(key)) continue;
      visited.add(key);
      if (++steps > 5000) throw new Error('TTS先読み: フローが大きすぎます（上限5000状態）');
      const node = byId.get(current.id);
      if (!node) continue;
      let values = [current.value];
      switch (node.type) {
        case 'textEdit': values = [node.data.text]; break;
        case 'qrScan': case 'listen': case 'tof': case 'counter': case 'randomChoice':
          values = [undefined]; break;
        case 'function':
          values = visit(node.data.internalNodes, node.data.internalEdges, current.value, depth + 1);
          break;
        case 'sayText': {
          const request = resolveSpeechRequest(current.value, node.data);
          if (typeof request !== 'string') requests.set(speechRequestKey(request), request);
          break;
        }
        case 'output': outputs.add(current.value); continue;
        case 'end': continue;
      }
      // Branches have no fixed runtime order. Collect their fixed candidates in edge order without executing them.
      for (const edge of [...(outs.get(node.id) ?? [])].reverse()) {
        for (const value of [...values].reverse()) pending.push({ id: edge.target, value });
      }
    }
    return outputs.size ? [...outputs] : [undefined];
  };
  visit(nodes, edges, typeof initialValue === 'string' || typeof initialValue === 'number' ? String(initialValue) : undefined, 0);
  return [...requests.values()];
}
